import * as React from "react"
import { router } from "@inertiajs/react"
import { Loader2, Telescope, X } from "lucide-react"
import { consumer } from "@/lib/actioncable"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { domainLabel } from "@/lib/accountSignals"

export type ResearchRun = {
  id: number
  status: string
  domains: string[]
  signals_count: number
  error_message: string | null
  created_at: string
}

export type ResearchState = {
  available: boolean
  // The domains the EXTERNAL-research picker may offer (P4): the full taxonomy
  // minus the research-excluded set (e.g. pricing_promotion). Manual signal
  // entry still uses the full taxonomy.
  research_domains: string[]
  weekly_domains: string[]
  latest_run: ResearchRun | null
}

type Broadcast = {
  run_id: number
  status: string
  type: string
  domain?: string
  signals_count?: number
  error_message?: string | null
}

const RUNNING = new Set(["pending", "processing"])

const STATUS_LABELS: Record<string, string> = {
  pending: "Antri",
  processing: "Berjalan",
  completed: "Selesai",
  failed: "Gagal",
  cancelled: "Dibatalkan",
}

export function ResearchPanel({
  accountId,
  research,
  domains,
  pendingResearchCount,
}: {
  accountId: number
  research: ResearchState
  domains: string[]
  pendingResearchCount: number
}) {
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [run, setRun] = React.useState<ResearchRun | null>(research.latest_run)
  const [liveCount, setLiveCount] = React.useState<number>(
    research.latest_run?.signals_count ?? 0,
  )
  const [failMessage, setFailMessage] = React.useState<string | null>(
    research.latest_run?.status === "failed"
      ? (research.latest_run.error_message ?? null)
      : null,
  )

  // Keep local state in sync when the server ships a fresh latest_run (e.g. after
  // the create redirect reloads props).
  React.useEffect(() => {
    setRun(research.latest_run)
    setLiveCount(research.latest_run?.signals_count ?? 0)
  }, [research.latest_run])

  const watching = run && RUNNING.has(run.status) ? run.id : null

  // Live progress over AccountResearchChannel (mirrors the NIQ reconcile flow).
  React.useEffect(() => {
    if (!watching) return
    const sub = consumer.subscriptions.create(
      { channel: "AccountResearchChannel", run_id: watching },
      {
        received(data: Broadcast) {
          if (data.run_id !== watching) return
          if (typeof data.signals_count === "number") setLiveCount(data.signals_count)
          switch (data.type) {
            case "research_failed":
              setFailMessage(data.error_message ?? "Terjadi kesalahan.")
              setRun((r) => (r ? { ...r, status: "failed" } : r))
              break
            case "research_complete":
            case "research_cancelled":
              setRun((r) => (r ? { ...r, status: data.status } : r))
              // Surface the new pending signals + run metadata.
              router.reload({ only: ["signals", "research"] })
              break
            default:
              setRun((r) => (r ? { ...r, status: data.status } : r))
          }
        },
      },
    )
    return () => {
      sub.unsubscribe()
    }
  }, [watching])

  const toggle = (domain: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(domain)) next.delete(domain)
      else next.add(domain)
      return next
    })
  }

  const startRun = () => {
    if (selected.size === 0) return
    setFailMessage(null)
    router.post(
      `/admin/accounts/${accountId}/research_runs`,
      { domains: Array.from(selected) },
      { preserveScroll: true, onSuccess: () => setSelected(new Set()) },
    )
  }

  const cancelRun = () => {
    if (!run) return
    router.patch(`/admin/accounts/research_runs/${run.id}/cancel`, {}, { preserveScroll: true })
  }

  const isRunning = run != null && RUNNING.has(run.status)

  if (!research.available) {
    return (
      <section className="mt-6 rounded-lg border border-dashed border-hairline bg-surface p-5">
        <div className="flex items-center gap-2 text-sm font-medium text-ink-muted">
          <Telescope className="h-4 w-4" />
          Riset Eksternal
        </div>
        <p className="mt-2 text-sm text-ink-muted">
          Mesin riset eksternal sedang nonaktif. Aktifkan dengan menyetel{" "}
          <code className="text-xs">ACCOUNT_RESEARCH=true</code> dan kredensial{" "}
          <code className="text-xs">anthropic_api_key</code>. Entri manual & semua tampilan
          tetap berfungsi.
        </p>
      </section>
    )
  }

  return (
    <section className="mt-6 rounded-lg border border-hairline bg-surface p-5">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-ink-muted">
            <Telescope className="h-4 w-4" />
            Riset Eksternal
          </div>
          <p className="mt-0.5 text-sm text-ink-muted">
            Pilih domain, lalu jalankan riset 360° (Claude + pencarian web). Hasil masuk
            sebagai sinyal <strong>menunggu review</strong>.
          </p>
        </div>
        {pendingResearchCount > 0 && (
          <Badge tone="signal">{pendingResearchCount} sinyal riset menunggu review</Badge>
        )}
      </div>

      {/* Domain picker */}
      <fieldset className="mt-4" disabled={isRunning}>
        <legend className="text-xs font-medium text-ink-muted">Domain riset</legend>
        <div className="mt-2 grid gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
          {domains.map((d) => (
            <label key={d} className="flex items-center gap-2 font-normal text-ink-body">
              <Checkbox checked={selected.has(d)} onChange={() => toggle(d)} />
              <span className="text-sm">{domainLabel(d)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Live status */}
      {run && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-page p-3 text-sm">
          {isRunning && <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />}
          <span className="text-ink-body">
            Riset terakhir: <strong>{STATUS_LABELS[run.status] ?? run.status}</strong>
          </span>
          <Badge tone="muted">{liveCount} sinyal</Badge>
          {run.status === "failed" && failMessage && (
            <span className="text-danger">· {failMessage}</span>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button onClick={startRun} disabled={isRunning || selected.size === 0}>
          <Telescope className="mr-2 h-4 w-4" />
          Jalankan riset{selected.size > 0 ? ` (${selected.size})` : ""}
        </Button>
        {isRunning && (
          <Button variant="ghost" onClick={cancelRun}>
            <X className="mr-1 h-4 w-4" />
            Batalkan
          </Button>
        )}
      </div>
    </section>
  )
}
